<template>
  <div class="map-full-container">
    <div id="container" class="amap-instance" />

    <div class="map-overlay-controls">
      <div v-if="enableAddPath || isDrawingCircle || isMarking || activeMeasureType" class="control-group tip-group">
        <i class="el-icon-info" />
        <span>{{ tipText }}</span>
      </div>

      <div class="control-group button-group-grid">
        <el-button
          type="success"
          size="small"
          icon="el-icon-download"
          @click="loadMarker"
        >
          加载标记
        </el-button>

        <el-button
          :type="isMarking ? 'success' : 'primary'"
          size="small"
          icon="el-icon-location-outline"
          @click="toggleMarkPlaceState"
        >
          {{ isMarking ? '保存并结束' : '标记地点' }}
        </el-button>

        <el-button
          type="info"
          size="small"
          icon="el-icon-guide"
          @click="onPathNavigator"
        >
          路径巡航
        </el-button>

        <el-button
          :type="enableAddPath ? 'success' : 'primary'"
          size="small"
          icon="el-icon-plus"
          @click="addPath"
        >
          {{ addPathText }}
        </el-button>

        <el-button
          :type="isDrawingCircle ? 'danger' : 'warning'"
          size="small"
          icon="el-icon-magic-stick"
          @click="toggleDrawCircleState"
        >
          {{ isDrawingCircle ? '结束画圆' : '绘制圆形' }}
        </el-button>

        <el-button
          :type="activeMeasureType === 'distance' ? 'danger' : 'warning'"
          size="small"
          icon="el-icon-odometer"
          @click="toggleMeasure('distance')"
        >
          {{ activeMeasureType === 'distance' ? '结束测距' : '测距离' }}
        </el-button>

        <el-button
          :type="activeMeasureType === 'area' ? 'danger' : 'warning'"
          size="small"
          icon="el-icon-crop"
          @click="toggleMeasure('area')"
        >
          {{ activeMeasureType === 'area' ? '结束测面' : '测面积' }}
        </el-button>
      </div>

      <div class="control-group single-button-row">
        <el-button
          type="danger"
          size="small"
          icon="el-icon-delete"
          style="width: 100%"
          @click="clearAll"
        >
          清除所有图形与测量
        </el-button>
      </div>

      <div v-if="isDrawingCircle" class="radius-mini-setter">
        <span class="label">默认半径(米):</span>
        <el-input-number
          v-model="radius"
          :min="10"
          :max="10000"
          size="mini"
          controls-position="right"
          style="width: 90px"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'
import { addGeoPath, getGeoPath, getMapMarks, addsMapMarks } from '@/api/geo'

export default {
  name: 'AMap',
  data() {
    return {
      amap: null,
      map: null,
      AMap: null,
      zoom: 12,
      center: [104.068071, 30.576432],
      path: [],
      mapCircle: null,
      circleEditor: null,
      radius: 1000,
      mapKeys: {
        securityJsCode: '983d6ee43bab3edf3693e91508f94aa9',
        key: '7b75ab2839ce68b884c7a682501ea774'
      },
      addPathText: '添加路径',
      enableAddPath: false,
      isDrawingCircle: false,
      isMarking: false,
      activeMeasureType: null,
      pathPointList: [],
      previewMarkers: [],
      previewPolyline: null,
      manualMarkers: [], // 临时存放本次批量标记在图面上的 Marker 实例对象
      tempMarkedPoints: [], // 👈 新增：专门用来传递给后端的纯坐标数据数组 [ {lng, lat}, ... ]
      loadedMarkers: [],
      rangingTool: null,
      mouseTool: null
    }
  },
  computed: {
    tipText() {
      if (this.isMarking) return `已标记 ${this.tempMarkedPoints.length} 个点，点击工具栏按钮可保存提交`
      if (this.isDrawingCircle) return '请在地图上点击某点作为圆心'
      if (this.enableAddPath) return '点击地图连续添加路径点'
      if (this.activeMeasureType === 'distance') return '单击开始测距，双击结束测距'
      if (this.activeMeasureType === 'area') return '单击绘制多边形，双击结束并计算面积'
      return ''
    }
  },
  mounted() {
    this.initAMap()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.destroy()
    }
  },
  methods: {
    initAMap() {
      window._AMapSecurityConfig = {
        securityJsCode: this.mapKeys.securityJsCode
      }
      AMapLoader.load({
        key: this.mapKeys.key,
        version: '1.4.15',
        plugins: ['AMap.CircleEditor', 'AMap.RangingTool', 'AMap.MouseTool'],
        AMapUI: {
          version: '1.0',
          plugins: ['misc/PathSimplifier']
        }
      }).then((AMap) => {
        this.AMap = AMap
        this.map = new AMap.Map('container', {
          viewMode: '2D',
          zoom: this.zoom,
          center: this.center,
          features: ['bg', 'point', 'road'],
          mapStyle: 'amap://styles/light'
        })

        this.rangingTool = new AMap.RangingTool(this.map)
        this.mouseTool = new AMap.MouseTool(this.map)

        this.mouseTool.on('draw', (e) => {})

        this.map.on('click', (e) => {
          if (this.activeMeasureType) return

          const lng = e.lnglat.getLng()
          const lat = e.lnglat.getLat()
          const currentIndex = this.pathPointList.length
          const currentTimestamp = new Date().getTime()
          if (this.enableAddPath) {
            const point = {
              index: currentIndex,
              lng: lng,
              lat: lat,
              timestamp: currentTimestamp
            }
            this.pathPointList.push(point)

            const marker = new AMap.Marker({
              position: e.lnglat,
              map: this.map,
              label: { content: this.pathPointList.length, direction: 'top' }
            })
            this.previewMarkers.push(marker)

            const pathArr = this.pathPointList.map(item => [item.lng, item.lat])
            if (this.previewPolyline) {
              this.previewPolyline.setPath(pathArr)
            } else {
              this.previewPolyline = new AMap.Polyline({
                path: pathArr,
                strokeColor: '#3366FF',
                strokeOpacity: 0.8,
                strokeWeight: 5,
                map: this.map
              })
            }
          } else if (this.isDrawingCircle) {
            this.drawCircle([lng, lat], this.radius)
          } else if (this.isMarking) {
            // 👈 修改：点击地图时同时记录图面实例与纯数据
            this.addManualMarker(e.lnglat)
          }
        })
      }).catch((e) => {
        console.error(e)
      })
    },

    loadMarker() {
      this.stopAllTools()
      this.activeMeasureType = null

      getMapMarks().then(resp => {
        if (resp.code === 0) {
          if (this.loadedMarkers.length > 0) {
            this.map.remove(this.loadedMarkers)
            this.loadedMarkers = []
          }

          for (const item of resp.data) {
            const lng = item.lng
            const lat = item.lat
            const title = item.title
            const id = item.id

            const marker = new this.AMap.Marker({
              position: [lng, lat],
              map: this.map,
              title: title,
              animation: 'AMAP_ANIMATION_DROP',
              extData: { id: id }
            })

            marker.on('click', () => {
              this.$message.info(`地点：${title} [${lng.toFixed(6)}, ${lat.toFixed(6)}]`)
            })

            this.loadedMarkers.push(marker)
          }
          this.$message.success(`成功加载了 ${resp.data.length} 个历史标记点`)
        }
      }).catch(error => {
        console.error('加载标记点失败：', error.message)
        this.$message.error('加载标记点失败')
      })
    },

    // 状态切换：标记地点 (包含批量提交后端的关键控制)
    toggleMarkPlaceState() {
      // 1. 如果当前处于标记状态，此时点击意味着“结束并保存”
      if (this.isMarking) {
        if (this.tempMarkedPoints.length === 0) {
          this.$message.warning('未选择任何标记点')
          this.isMarking = false
          return
        }

        // 发送保存数组到后端
        addsMapMarks(this.tempMarkedPoints).then(resp => {
          if (resp.code === 0) {
            this.$message.success('批量标记地点保存成功！')
          }
        }).catch(err => {
          console.error(err)
          this.$message.error('保存标记点失败')
        }).finally(() => {
          this.isMarking = false
          this.tempMarkedPoints = []
          this.clearManualMarkers()
        })
        return
      }

      // 2. 如果是从其他状态切换过来，初始化环境
      this.stopAllTools()
      this.activeMeasureType = null
      this.isMarking = true
      this.clearManualMarkers()
      this.$message.info('已开启标记模式，请在地图上点击打点')
    },

    // 往临时数组和图面上同步添加数据
    addManualMarker(lnglat) {
      const lng = lnglat.getLng()
      const lat = lnglat.getLat()
      const currentIndex = this.tempMarkedPoints.length
      const currentTimestamp = new Date().getTime()

      // 1. 把纯数据压入准备发往后端的数组中
      this.tempMarkedPoints.push({
        index: currentIndex,
        lng: lng,
        lat: lat,
        timestamp: currentTimestamp
      })

      // 2. 在图面上渲染临时图标提供视觉反馈
      const marker = new this.AMap.Marker({
        position: lnglat,
        map: this.map,
        animation: 'AMAP_ANIMATION_DROP',
        title: `待保存点 [${lng.toFixed(6)}, ${lat.toFixed(6)}]`
      })

      this.manualMarkers.push(marker)
    },

    // 清空本次未保存的临时标记图层
    clearManualMarkers() {
      if (this.manualMarkers.length > 0) {
        this.map.remove(this.manualMarkers)
        this.manualMarkers = []
      }
      this.tempMarkedPoints = []
    },

    toggleMeasure(type) {
      if (this.activeMeasureType === type) {
        this.stopAllTools()
        this.activeMeasureType = null
        return
      }

      this.stopAllTools()
      this.activeMeasureType = type

      if (type === 'distance') {
        if (this.rangingTool) this.rangingTool.turnOn()
      } else if (type === 'area') {
        if (this.mouseTool) {
          this.mouseTool.measureArea({
            strokeColor: '#fc5531',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#fc5531',
            fillOpacity: 0.2
          })
        }
      }
    },

    stopAllTools() {
      this.enableAddPath = false
      this.addPathText = '添加路径'

      // 注意：如果是切换到别的工具导致退出标记状态，视为放弃未保存的打点，执行擦除
      if (this.isMarking) {
        this.clearManualMarkers()
      }
      this.isMarking = false
      this.isDrawingCircle = false

      if (this.rangingTool) this.rangingTool.turnOff()
      if (this.mouseTool) this.mouseTool.close(false)
    },

    toggleDrawCircleState() {
      const targetState = !this.isDrawingCircle
      this.stopAllTools()
      this.activeMeasureType = null
      this.isDrawingCircle = targetState
    },

    addPath() {
      if (!this.enableAddPath) {
        this.stopAllTools()
        this.activeMeasureType = null
        this.enableAddPath = true
        this.addPathText = '保存路径'
        this.clearPreviewOverlays()
        this.pathPointList = []
      } else {
        if (this.pathPointList.length < 2) {
          this.$message.warning('请至少点击两个点')
          return
        }
        this.enableAddPath = false
        this.addPathText = '添加路径'
        addGeoPath(this.pathPointList).then(resp => {
          if (resp.code === 0) this.$message.success('路径保存成功')
        }).finally(() => {
          this.clearPreviewOverlays()
          this.pathPointList = []
        })
      }
    },

    drawCircle(pointArr, radius) {
      this.clearCircle()
      const center = new this.AMap.LngLat(pointArr[0], pointArr[1])
      const circle = new this.AMap.Circle({
        center: center,
        radius: radius,
        strokeColor: '#409EFF',
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: '#409EFF',
        fillOpacity: 0.2,
        zIndex: 50
      })
      this.map.add(circle)
      this.map.setFitView([circle])
      this.mapCircle = circle

      const self = this
      this.map.plugin(['AMap.CircleEditor'], function() {
        const circleEditor = new self.AMap.CircleEditor(self.map, circle)
        circleEditor.open()
        self.circleEditor = circleEditor
      })
    },

    onPathNavigator() {
      this.stopAllTools()
      this.activeMeasureType = null

      var queryParam = {
        pathId: 1
      }
      getGeoPath(queryParam).then(resp => {
        if (resp.code === 0) {
          this.path = resp.data
          if (this.path.length === 0) return this.$message.warning('暂无路径')

          AMapUI.load(['ui/misc/PathSimplifier'], (PathSimplifier) => {
            const pathSimplifierIns = new PathSimplifier({
              map: this.map,
              zIndex: 100,
              data: this.path,
              getPath: (pathData) => pathData.path
            })

            // 单位 km/h, 这里设置为 1000m/s
            const speed = 3600
            const navg = pathSimplifierIns.createPathNavigator(0, { loop: false, speed: speed })
            navg.start()
          })
        }
      })
    },

    clearPreviewOverlays() {
      if (this.previewMarkers.length > 0) {
        this.map.remove(this.previewMarkers)
        this.previewMarkers = []
      }
      if (this.previewPolyline) {
        this.map.remove(this.previewPolyline)
        this.previewPolyline = null
      }
    },

    clearCircle() {
      if (this.mapCircle) this.map.remove(this.mapCircle)
      if (this.circleEditor) this.circleEditor.close()
      this.mapCircle = null
      this.circleEditor = null
    },

    clearAll() {
      this.stopAllTools()
      this.activeMeasureType = null

      this.clearCircle()
      this.clearPreviewOverlays()
      this.clearManualMarkers()

      if (this.loadedMarkers.length > 0) {
        this.map.remove(this.loadedMarkers)
        this.loadedMarkers = []
      }

      if (this.rangingTool) this.rangingTool.turnOff()
      if (this.mouseTool) this.mouseTool.close(true)

      this.pathPointList = []
      this.$message.info('图面覆盖物与测量数据已完全清空')
    }
  }
}
</script>

<style scoped lang="scss">
.map-full-container {
  width: 100%;
  height: 100%; /* 改为 100%，撑满父级 .content-view */
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.amap-instance {
  width: 100%;
  height: 100%;
}

.map-overlay-controls {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  .tip-group {
    background: rgba(255, 255, 255, 0.95);
    padding: 6px 14px;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    color: #e6a23c;
    font-size: 12px;
    border-left: 4px solid #e6a23c;
    display: flex;
    align-items: center;
    i { margin-right: 6px; }
  }

  .button-group-grid {
    background: rgba(255, 255, 255, 0.9);
    padding: 8px 8px 4px 8px;
    border-radius: 6px 6px 0 0;
    box-shadow: 0 -2px 12px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #dcdfe6;
    border-bottom: none;

    display: grid;
    grid-template-columns: repeat(2, 110px);
    gap: 6px;

    .el-button {
      margin: 0 !important;
      width: 100%;
      height: 32px;
      padding: 0 4px;
      font-size: 12px;
      border-radius: 4px;
    }
  }

  .single-button-row {
    background: rgba(255, 255, 255, 0.9);
    padding: 0 8px 8px 8px;
    border-radius: 0 0 6px 6px;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
    border: 1px solid #dcdfe6;
    border-top: none;
    width: 236px;
    box-sizing: border-box;

    .el-button {
      height: 32px;
      font-size: 12px;
    }
  }

  .radius-mini-setter {
    background: #409eff;
    color: #ffffff;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 10px rgba(64, 158, 255, 0.3);
    .label { font-weight: bold; }

    ::v-deep .el-input-number--mini {
      .el-input__inner {
        padding-left: 5px;
        padding-right: 25px;
        height: 24px;
        line-height: 24px;
      }
    }
  }
}
</style>
